import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';
import { getSortKey } from './shared';

export const evaluateDominanceSummary: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode } = context;
    const spotForLogic = data.spotPrice || 0;
    if (spotForLogic <= 0) return null;
    
    const atmStrike = Math.round(spotForLogic / gap) * gap;
    const minStrikeBound = atmStrike - (activeRange * gap);
    const maxStrikeBound = atmStrike + (activeRange * gap);

    const rawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const flatOpts = ensureFlatOpts(rawOptsArr);

    const filteredRows = flatOpts.filter((opt: any) => opt.strike >= minStrikeBound && opt.strike <= maxStrikeBound);
    filteredRows.sort((a: any, b: any) => getSortKey(b, settingsMode) - getSortKey(a, settingsMode));

    const actualRows = filteredRows.slice(0, 5); // Defaulting to Top 5 like UI's bbTop5
    
    let bullCount = 0;
    let bearCount = 0;
    let bullVol = 0;
    let bearVol = 0;

    for (const r of actualRows) {
        const isCE = r.type === 'CE';
        const isATM = r.strike === spotForLogic || r.strike === atmStrike;
        const isITM = !isATM && (isCE ? r.strike < spotForLogic : r.strike > spotForLogic);
        const isOTM = !isATM && (isCE ? r.strike > spotForLogic : r.strike < spotForLogic);

        const vol = r.volume || r.combVol || 0;

        if ((isCE && isITM) || (!isCE && isOTM) || (isCE && isATM)) {
            bullCount++;
            bullVol += vol;
        } else if ((!isCE && isITM) || (isCE && isOTM) || (!isCE && isATM)) {
            bearCount++;
            bearVol += vol;
        }
    }

    const countDiff = bullCount - bearCount;
    const volDiff = bullVol - bearVol;

    let status = 'Sideways';
    if (countDiff > 0 || (countDiff === 0 && volDiff > 0)) status = 'BULL';
    else if (countDiff < 0 || (countDiff === 0 && volDiff < 0)) status = 'BEAR';

    if (status === 'Sideways') return null;

    // Resolve Entry
    const entryStrike = atmStrike;
    let entryPrice = 0;
    const optionType = status === 'BULL' ? 'CE' : 'PE';

    const atmRow = flatOpts.find((r: any) => r.strike === entryStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }

    if (entryPrice <= 0) return null;

    return {
        trade_type: status as any,
        strike: entryStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `Dominance Top5 (Bull:${bullCount} Bear:${bearCount})`
    };
};
